import { addIceSafe, getIceConfig, loadIceConfig, packIce, packSdp } from './iceConfig'

const shouldOffer = (myId, theirId) => String(myId) < String(theirId)

export const createRoomRtc = ({ getSocket, roomId, myId, onError, onNeedTap }) => {
  const peers = new Map()
  const remoteAudio = new Map()
  const pendingIce = new Map()
  let localStream = null
  let blocked = false

  const notifyBlocked = (isBlocked) => {
    blocked = Boolean(isBlocked)
    if (onNeedTap) onNeedTap(blocked)
  }

  const playAudio = (audio) => {
    if (!audio) return
    audio.muted = false
    audio.volume = 1
    const play = audio.play()
    if (play && play.then) {
      play.then(() => notifyBlocked(false)).catch(() => notifyBlocked(true))
    }
  }

  const attachAudio = (peerId, track, stream) => {
    const key = String(peerId)
    let audio = remoteAudio.get(key)
    if (!audio) {
      audio = document.createElement('audio')
      audio.autoplay = true
      audio.playsInline = true
      audio.setAttribute('autoplay', '')
      audio.setAttribute('playsinline', '')
      audio.setAttribute('webkit-playsinline', '')
      audio.setAttribute('data-room-peer', key)
      document.body.appendChild(audio)
      remoteAudio.set(key, audio)
    }
    const next = stream && stream.getTracks && stream.getTracks().length
      ? stream
      : (audio.srcObject instanceof MediaStream ? audio.srcObject : new MediaStream())
    if (!stream) {
      const current = next.getTracks().find(item => item.kind === track.kind)
      if (current && current !== track) next.removeTrack(current)
      if (!next.getTracks().includes(track)) next.addTrack(track)
    }
    audio.srcObject = next
    playAudio(audio)
  }

  const send = (to, payload) => {
    getSocket().emit('room:signal', { roomId, to, payload })
  }

  const queueIce = (peerId, candidate) => {
    const key = String(peerId)
    const list = pendingIce.get(key) || []
    list.push(candidate)
    pendingIce.set(key, list)
  }

  const flushIce = async (peerId) => {
    const key = String(peerId)
    const pc = peers.get(key)
    const queued = pendingIce.get(key) || []
    if (!pc || !pc.remoteDescription || !queued.length) return
    pendingIce.set(key, [])
    for (const candidate of queued) {
      await addIceSafe(pc, candidate)
    }
  }

  const addLocalTracks = (pc) => {
    if (!pc || !localStream) return false
    const track = localStream.getAudioTracks()[0]
    if (!track) return false
    const sender = pc.getSenders().find(item => item.track && item.track.kind === 'audio')
      || pc.getSenders().find(item => !item.track)
    if (sender) {
      if (sender.track !== track) {
        sender.replaceTrack(track)
        return true
      }
      return false
    }
    pc.addTrack(track, localStream)
    return true
  }

  const makePeer = (peerId) => {
    const key = String(peerId)
    let pc = peers.get(key)
    if (pc) return pc
    pc = new RTCPeerConnection(getIceConfig())
    peers.set(key, pc)
    addLocalTracks(pc)
    pc.onicecandidate = (event) => {
      const packed = packIce(event.candidate)
      if (packed && packed.candidate) send(peerId, { type: 'ice', candidate: packed })
    }
    pc.ontrack = (event) => {
      const track = event.track || (event.streams[0] && event.streams[0].getTracks()[0])
      if (track) attachAudio(peerId, track, event.streams && event.streams[0])
    }
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed') {
        try { pc.restartIce() } catch (err) {}
      }
      if (pc.connectionState === 'closed') closePeer(peerId)
    }
    return pc
  }

  const closePeer = (peerId) => {
    const key = String(peerId)
    pendingIce.delete(key)
    const pc = peers.get(key)
    if (pc) {
      pc.onicecandidate = null
      pc.ontrack = null
      pc.onconnectionstatechange = null
      pc.close()
      peers.delete(key)
    }
    const audio = remoteAudio.get(key)
    if (audio) {
      audio.pause()
      audio.srcObject = null
      audio.remove()
      remoteAudio.delete(key)
    }
  }

  const renegotiate = async (peerId) => {
    const pc = peers.get(String(peerId))
    if (!pc || pc.signalingState !== 'stable') return
    addLocalTracks(pc)
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    send(peerId, { type: 'offer', sdp: packSdp(pc.localDescription) })
  }

  const offerTo = async (peerId) => {
    const key = String(peerId)
    const existing = peers.get(key)
    if (existing) {
      if (existing.signalingState !== 'stable') return
      if (existing.connectionState === 'connected' || existing.connectionState === 'connecting') {
        addLocalTracks(existing)
        return
      }
    }
    const pc = makePeer(peerId)
    addLocalTracks(pc)
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    send(peerId, { type: 'offer', sdp: packSdp(pc.localDescription) })
  }

  const handleSignal = async ({ from, payload }) => {
    if (!from || !payload || String(from) === String(myId)) return
    const key = String(from)
    try {
      if (payload.type === 'offer') {
        const pc = makePeer(from)
        if (pc.signalingState === 'have-local-offer') {
          await pc.setLocalDescription({ type: 'rollback' }).catch(() => {})
        }
        await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp))
        addLocalTracks(pc)
        const answer = await pc.createAnswer()
        await pc.setLocalDescription(answer)
        send(from, { type: 'answer', sdp: packSdp(pc.localDescription) })
        await flushIce(from)
      } else if (payload.type === 'answer') {
        const pc = peers.get(key)
        if (!pc || pc.signalingState !== 'have-local-offer') return
        await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp))
        await flushIce(from)
      } else if (payload.type === 'ice' && payload.candidate) {
        const pc = peers.get(key)
        if (!pc || !pc.remoteDescription) {
          queueIce(from, payload.candidate)
          return
        }
        await addIceSafe(pc, payload.candidate)
      }
    } catch (err) {
      const msg = String((err && err.message) || '')
      if (onError && !/ice|candidate/i.test(msg)) onError(msg)
    }
  }

  const startMic = async () => {
    await loadIceConfig()
    if (localStream && localStream.getAudioTracks().some(track => track.readyState === 'live')) {
      unlockPlayback()
      return localStream
    }
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop())
      localStream = null
    }
    localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      },
      video: false
    })
    localStream.getAudioTracks().forEach(track => { track.enabled = false })
    unlockPlayback()

    const renegotiateTasks = []
    peers.forEach((pc, peerId) => {
      const added = addLocalTracks(pc)
      if (added && pc.signalingState === 'stable' && pc.remoteDescription) {
        renegotiateTasks.push(renegotiate(peerId).catch(err => onError && onError(err.message)))
      }
    })
    await Promise.all(renegotiateTasks)
    return localStream
  }

  const sync = async (members) => {
    const others = (members || []).filter(member => String(member.id) !== String(myId))
    const live = new Set(others.map(member => String(member.id)))
    ;[...peers.keys()].forEach(peerId => {
      if (!live.has(String(peerId))) closePeer(peerId)
    })
    if (!others.length) return

    try {
      await startMic()
    } catch (err) {
      if (onError) onError(err.message)
      return
    }

    await Promise.all(others.map(member => {
      if (!shouldOffer(myId, member.id)) return null
      return offerTo(member.id)
    }))
  }

  const setMuted = (muted) => {
    if (!localStream) return
    localStream.getAudioTracks().forEach(track => { track.enabled = !muted })
  }

  const unlockPlayback = () => {
    remoteAudio.forEach(playAudio)
    if (!remoteAudio.size) notifyBlocked(false)
  }

  const stopMic = () => {
    if (!localStream) return
    localStream.getTracks().forEach(track => track.stop())
    localStream = null
  }

  const destroy = () => {
    stopMic()
    ;[...peers.keys()].forEach(closePeer)
    pendingIce.clear()
  }

  return { handleSignal, sync, startMic, stopMic, setMuted, unlockPlayback, destroy }
}
